import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { FolderOpen, Plus, Eye, EyeOff, Copy, Search, Filter, Download } from 'lucide-react';
import { Container, PageHeader, Toolbar } from '@vnetwork/ui/layout';
import { Button, Badge, IconButton, Input, Skeleton, Dialog, DialogContent, DialogHeader, DialogTitle, DialogBody, DialogFooter, DialogClose, Popover, PopoverTrigger, PopoverContent } from '@vnetwork/ui/primitives';
import { DataTable, type ColumnDef, StatusPill, EmptyState, FilterBuilder, type FilterField, type FilterCondition } from '@vnetwork/ui/data';
import { ConfirmDialog, Loading, useToast } from '@vnetwork/ui/feedback';
import {
  useWatchFolders,
  useWatchFolderDetail,
  useWatchFolderStatus,
  useDeleteWatchFolder,
  useEnableWatchFolder,
} from '@/api/media/vod-watch-folders';
import type { WatchFolder } from '@/api/media/vod-watch-folders-types';
import { VodConsoleBreadcrumbs } from '../VodConsoleBreadcrumbs';
import { RowMenu } from '../../components/RowMenu';
import { RefreshButton } from '../../_shared/AnalyticsKit';
import styles from './WatchFoldersList.module.css';

// ─── Status helpers ───────────────────────────────────────────────────────────

function watchFolderStatusTone(status: number): 'success' | 'warning' | 'danger' | 'neutral' {
  if (status === 1) return 'success';
  if (status === 2) return 'warning';
  return 'danger';
}

function watchFolderStatusLabel(status: number): string {
  if (status === 1) return 'Active';
  if (status === 2) return 'Paused';
  return 'Inactive';
}

const boolLabel = (v?: boolean) => (v ? 'Yes' : 'No');

// Status filter key (active/paused/inactive) — mirrors the label/tone helpers.
function watchFolderStatusKey(status: number): 'active' | 'paused' | 'inactive' {
  if (status === 1) return 'active';
  if (status === 2) return 'paused';
  return 'inactive';
}

/** Download the given watch folders as a CSV file. */
function exportCsv(rows: WatchFolder[]) {
  const head = ['id', 'name', 'status', 'path_check', 'updated_at'];
  const esc = (v: unknown) => `"${String(v ?? '').replace(/"/g, '""')}"`;
  const body = rows
    .map((r) =>
      [r.id, r.name, watchFolderStatusLabel(r.status), boolLabel(r.path_check), r.updated_at ? new Date(r.updated_at).toISOString() : '']
        .map(esc)
        .join(','),
    )
    .join('\n');
  const blob = new Blob([`${head.join(',')}\n${body}`], { type: 'text/csv' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `watch-folders-${rows.length}.csv`;
  a.click();
  URL.revokeObjectURL(url);
}

// ─── Detail dialog ────────────────────────────────────────────────────────────

function SensitiveValue({ value }: { value?: string | null }) {
  const [revealed, setRevealed] = useState(false);
  const toast = useToast();
  if (!value) return <span className={styles.cellMono}>—</span>;
  const display = revealed ? value : `${value.slice(0, 3)}••••${value.slice(-3)}`;
  return (
    <span className={styles.secretRow}>
      <span>{display}</span>
      <IconButton size="sm" aria-label={revealed ? 'Hide' : 'Reveal'} onClick={() => setRevealed((p) => !p)}>
        {revealed ? <EyeOff size={13} /> : <Eye size={13} />}
      </IconButton>
      <IconButton size="sm" aria-label="Copy" onClick={() => { navigator.clipboard.writeText(value); toast.success('Copied'); }}>
        <Copy size={13} />
      </IconButton>
    </span>
  );
}

function DetailRow({ label, value }: { label: string; value?: React.ReactNode }) {
  return (
    <dl className={styles.detailRow}>
      <dt>{label}</dt>
      <dd>{value ?? '—'}</dd>
    </dl>
  );
}

function WatchFolderDetailDialog({ serviceId, id, onClose }: { serviceId: string; id: number; onClose: () => void }) {
  const { data, isLoading } = useWatchFolderDetail(serviceId, id);
  const ing = data?.ingestion_config;
  const tc = data?.transcoding_configs?.[0];
  const dist = tc?.distribution_configs?.[0];
  const preset = data?.preset_configs?.[0];

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent size="lg">
        <DialogHeader>
          <DialogTitle>Watch Folder Details</DialogTitle>
        </DialogHeader>
        <DialogBody>
          {isLoading ? (
            <Loading label="Loading…" size="sm" />
          ) : !data ? (
            <span className={styles.cellMono}>Not found</span>
          ) : (
            <div className={styles.detailPanel}>
              <p className={styles.sectionTitle}>General</p>
              <DetailRow label="ID" value={<span className={styles.cellMono}>{data.id}</span>} />
              <DetailRow label="Name" value={data.name} />
              <DetailRow label="Status" value={<StatusPill tone={watchFolderStatusTone(data.status)}>{watchFolderStatusLabel(data.status)}</StatusPill>} />
              <DetailRow label="Strict" value={boolLabel(data.strict)} />
              <DetailRow label="Path Check" value={boolLabel(data.path_check)} />
              <DetailRow label="Transmux Only" value={boolLabel(data.transmux_only)} />
              <DetailRow label="Audio Only" value={boolLabel(data.audio_only)} />
              <DetailRow label="Updated" value={data.updated_at ? new Date(data.updated_at).toLocaleString() : '—'} />

              {ing && (
                <>
                  <p className={`${styles.sectionTitle} ${styles.sectionTitleSpaced}`}>Ingestion</p>
                  <DetailRow label="Host" value={<span className={styles.cellMono}>{ing.host}</span>} />
                  <DetailRow label="Port" value={ing.port ?? '—'} />
                  <DetailRow label="Path" value={<span className={styles.cellMono}>{ing.file_path}</span>} />
                  <DetailRow label="Auth type" value={ing.auth_type} />
                  <DetailRow label="Username" value={ing.username ?? '—'} />
                  <DetailRow label="Password" value={<SensitiveValue value={ing.password} />} />
                  {ing.bucket && <DetailRow label="Bucket" value={<span className={styles.cellMono}>{ing.bucket}</span>} />}
                  {ing.region && <DetailRow label="Region" value={<span className={styles.cellMono}>{ing.region}</span>} />}
                  {ing.access_key_id && <DetailRow label="Access Key ID" value={<SensitiveValue value={ing.access_key_id} />} />}
                  {ing.secret_access_key && <DetailRow label="Secret Access Key" value={<SensitiveValue value={ing.secret_access_key} />} />}
                </>
              )}

              {preset && (
                <>
                  <p className={`${styles.sectionTitle} ${styles.sectionTitleSpaced}`}>Preset</p>
                  <DetailRow label="Preset ID" value={preset.preset_id} />
                  <DetailRow label="Preset Name" value={preset.preset_name} />
                  <DetailRow label="Predefined" value={boolLabel(preset.preset_predefined)} />
                </>
              )}

              {tc && (
                <>
                  <p className={`${styles.sectionTitle} ${styles.sectionTitleSpaced}`}>Transcoding</p>
                  <DetailRow label="Format" value={tc.format} />
                  <DetailRow label="Fragment MP4" value={boolLabel(tc.fragment_mp4)} />
                  <DetailRow label="Has Thumbnail" value={boolLabel(tc.has_thumbnail)} />
                  <DetailRow label="Mix Audio/Video" value={boolLabel(tc.mix_audio_video)} />
                  <DetailRow label="Pass" value={tc.pass} />
                </>
              )}

              {dist && (
                <>
                  <p className={`${styles.sectionTitle} ${styles.sectionTitleSpaced}`}>Distribution</p>
                  <DetailRow label="Type" value={
                    <Badge tone="neutral" appearance="subtle" size="sm" style={{ fontFamily: 'var(--vn-font-mono)' }}>
                      Type #{dist.distribution_type}
                    </Badge>
                  } />
                  {dist.host && <DetailRow label="Host" value={<span className={styles.cellMono}>{dist.host}</span>} />}
                  {dist.port != null && <DetailRow label="Port" value={dist.port} />}
                  {dist.username && <DetailRow label="Username" value={dist.username} />}
                  {dist.password && <DetailRow label="Password" value={<SensitiveValue value={dist.password} />} />}
                  {dist.path && <DetailRow label="Path" value={<span className={styles.cellMono}>{dist.path}</span>} />}
                  {dist.bucket && <DetailRow label="Bucket" value={dist.bucket} />}
                  {dist.region && <DetailRow label="Region" value={dist.region} />}
                  {dist.access_key_id && <DetailRow label="Access Key ID" value={<SensitiveValue value={dist.access_key_id} />} />}
                  {dist.secret_access_key && <DetailRow label="Secret Access Key" value={<SensitiveValue value={dist.secret_access_key} />} />}
                </>
              )}
            </div>
          )}
        </DialogBody>
        <DialogFooter>
          <DialogClose asChild><Button variant="secondary">Close</Button></DialogClose>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ─── Error status dialog ──────────────────────────────────────────────────────

function WatchFolderErrorStatusDialog({ serviceId, id, onClose }: { serviceId: string; id: number; onClose: () => void }) {
  const { data, isLoading } = useWatchFolderStatus(serviceId, id);

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent size="sm">
        <DialogHeader>
          <DialogTitle>Error Status</DialogTitle>
        </DialogHeader>
        <DialogBody>
          {isLoading ? (
            <Loading label="Loading…" size="sm" />
          ) : (
            <div className={styles.detailPanel}>
              <p className={styles.errorLabel}>Error Code</p>
              <span className={styles.cellMono}>{(data as Record<string, unknown>)?.error_code as string ?? '—'}</span>
              <p className={`${styles.errorLabel} ${styles.sectionTitleSpaced}`}>Error Message</p>
              <span className={styles.errorBox}>{(data as Record<string, unknown>)?.error_msg as string ?? 'No error message.'}</span>
            </div>
          )}
        </DialogBody>
        <DialogFooter>
          <DialogClose asChild><Button variant="secondary">Close</Button></DialogClose>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ─── Main list component ──────────────────────────────────────────────────────

export function VodWatchFoldersList({ serviceId }: { serviceId: string }) {
  const navigate = useNavigate();
  const toast = useToast();

  const { data: watchFolders = [], isLoading, refetch } = useWatchFolders(serviceId);
  const deleteMutation = useDeleteWatchFolder(serviceId);
  const enableMutation = useEnableWatchFolder(serviceId);

  const [detailId, setDetailId] = useState<number | null>(null);
  const [errorStatusId, setErrorStatusId] = useState<number | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<WatchFolder | null>(null);
  const [search, setSearch] = useState('');
  const [conditions, setConditions] = useState<FilterCondition[]>([]);
  const [filterOpen, setFilterOpen] = useState(false);

  // Active status scope derived from the FilterBuilder conditions (multiselect
  // values are comma-joined per row, so split + flatten across matching rows).
  const statusFilters = useMemo(() => {
    const vals = conditions
      .filter((c) => c.field === 'status')
      .flatMap((c) => (c.value || '').split(','))
      .map((s) => s.trim())
      .filter(Boolean);
    return new Set(vals);
  }, [conditions]);

  const filterFields: FilterField[] = [
    {
      value: 'status',
      label: 'Status',
      input: 'multiselect',
      operators: [{ value: 'is_any_of', label: 'is any of' }],
      options: [
        { value: 'active', label: 'Active' },
        { value: 'paused', label: 'Paused' },
        { value: 'inactive', label: 'Inactive' },
      ],
    },
  ];

  const rows = useMemo(() => {
    let r = watchFolders;
    if (statusFilters.size) r = r.filter((x) => statusFilters.has(watchFolderStatusKey(x.status)));
    if (search.trim()) {
      const q = search.toLowerCase();
      r = r.filter((x) => (x.name ?? '').toLowerCase().includes(q));
    }
    return r;
  }, [watchFolders, statusFilters, search]);

  const handleEnable = (row: WatchFolder) => {
    enableMutation.mutate(
      { watchFolderId: row.id },
      {
        onSuccess: () => toast.success('Watch folder enabled'),
        onError: (err) => toast.error((err as Error).message || 'Failed to enable watch folder'),
      },
    );
  };

  const handleDeleteConfirm = () => {
    if (!deleteTarget) return;
    deleteMutation.mutate(
      { watchFolderId: deleteTarget.id },
      {
        onSuccess: () => { toast.success('Watch folder deleted'); setDeleteTarget(null); },
        onError: (err) => { toast.error((err as Error).message || 'Failed to delete watch folder'); setDeleteTarget(null); },
      },
    );
  };

  const columns: ColumnDef<WatchFolder, unknown>[] = [
    {
      id: 'id',
      header: 'ID',
      size: 80,
      cell: ({ row }) => (
        <button className={styles.cellLink} onClick={() => setDetailId(row.original.id)}>
          {row.original.id}
        </button>
      ),
    },
    { accessorKey: 'name', header: 'Name', size: 220 },
    {
      id: 'status',
      header: 'Status',
      size: 120,
      cell: ({ row }) => (
        <StatusPill tone={watchFolderStatusTone(row.original.status)}>
          {watchFolderStatusLabel(row.original.status)}
        </StatusPill>
      ),
    },
    {
      id: 'path_check',
      header: 'Path Check',
      size: 110,
      cell: ({ row }) => <span className={styles.cellMono}>{boolLabel(row.original.path_check)}</span>,
    },
    {
      id: 'updated_at',
      header: 'Updated',
      size: 170,
      cell: ({ row }) => (
        <span className={styles.cellMono}>
          {row.original.updated_at ? new Date(row.original.updated_at).toLocaleString() : '—'}
        </span>
      ),
    },
    {
      id: 'actions',
      header: '',
      size: 150,
      cell: ({ row }) => {
        const inactive = row.original.status !== 1;
        return (
          <div className={styles.actionRow}>
            {inactive && (
              <Button
                size="sm"
                variant="secondary"
                disabled={enableMutation.isPending}
                onClick={(e) => { e.stopPropagation(); handleEnable(row.original); }}
              >
                Enable
              </Button>
            )}
            <RowMenu
              items={[
                ...(inactive
                  ? [{ label: 'Error Status', onSelect: () => setErrorStatusId(row.original.id) }]
                  : []),
                { label: 'Delete', danger: true, onSelect: () => setDeleteTarget(row.original) },
              ]}
            />
          </div>
        );
      },
    },
  ];

  const loading = isLoading;

  return (
    <Container size="xl">
      <PageHeader
        breadcrumbs={<VodConsoleBreadcrumbs trail={[{ label: 'Watch Folders' }]} />}
        title={`Watch Folders${watchFolders.length ? ` (${watchFolders.length})` : ''}`}
        description="Folder-based ingestion configs and linked transcoding settings."
        actions={
          <Button
            variant="primary"
            startIcon={<Plus size={15} />}
            onClick={() => navigate(`/services/vod/${serviceId}/watch-folders/create`)}
          >
            Create Watch Folder
          </Button>
        }
      />

      <Toolbar
        start={
          <Input
            placeholder="Search by name…"
            startSlot={<Search size={15} />}
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            wrapperClassName={styles.searchInput}
          />
        }
        end={
          <>
            <RefreshButton onRefresh={() => void refetch()} />
            <Popover open={filterOpen} onOpenChange={setFilterOpen}>
              <PopoverTrigger asChild>
                <Button variant="secondary" startIcon={<Filter size={15} />}>
                  Filters{conditions.length ? ` (${conditions.length})` : ''}
                </Button>
              </PopoverTrigger>
              <PopoverContent align="start" style={{ padding: 0 }}>
                <FilterBuilder
                  fields={filterFields}
                  defaultValue={conditions.length ? conditions : undefined}
                  onApply={(c) => {
                    setConditions(c.filter((x) => x.value.trim()));
                    setFilterOpen(false);
                  }}
                  onClose={() => setFilterOpen(false)}
                />
              </PopoverContent>
            </Popover>
            {conditions.length > 0 && (
              <Button variant="ghost" onClick={() => setConditions([])}>Clear</Button>
            )}
            <Button
              variant="secondary"
              startIcon={<Download size={15} />}
              disabled={rows.length === 0}
              onClick={() => exportCsv(rows)}
            >
              Export
            </Button>
          </>
        }
      />

      {loading ? (
        <div style={{ display: 'grid', gap: 'var(--vn-space-2)' }}>
          {Array.from({ length: 5 }).map((_, i) => <Skeleton key={i} variant="rect" height={44} />)}
        </div>
      ) : (
        <DataTable
          columns={columns}
          data={rows}
          getRowId={(r) => String(r.id)}
          onRowClick={(row) => setDetailId(row.id)}
          emptyState={
            // First-use shows a creation hint; once folders exist the empty state
            // means the search/filters excluded everything.
            watchFolders.length === 0 ? (
              <EmptyState
                icon={<FolderOpen size={20} />}
                title="No watch folders"
                description="Create a watch folder to automate ingestion."
                actions={
                  <Button
                    size="sm"
                    startIcon={<Plus size={14} />}
                    onClick={() => navigate(`/services/vod/${serviceId}/watch-folders/create`)}
                  >
                    Create Watch Folder
                  </Button>
                }
              />
            ) : (
              'No watch folders match your search or filters.'
            )
          }
          footer={watchFolders.length ? `Showing ${rows.length} of ${watchFolders.length}` : undefined}
        />
      )}

      {detailId !== null && (
        <WatchFolderDetailDialog
          serviceId={serviceId}
          id={detailId}
          onClose={() => setDetailId(null)}
        />
      )}

      {errorStatusId !== null && (
        <WatchFolderErrorStatusDialog
          serviceId={serviceId}
          id={errorStatusId}
          onClose={() => setErrorStatusId(null)}
        />
      )}

      <ConfirmDialog
        open={deleteTarget !== null}
        onOpenChange={(o) => !o && setDeleteTarget(null)}
        title="Delete Watch Folder"
        description={`This will permanently delete "${deleteTarget?.name}". This action cannot be undone.`}
        confirmLabel="Delete"
        destructive
        loading={deleteMutation.isPending}
        onConfirm={handleDeleteConfirm}
        requireText={deleteTarget?.name}
      />
    </Container>
  );
}
